import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { useAuth } from '@/auth/context'
import { safeStorage } from '@/lib/storage'
import { SessionExpired } from '@/auth/SessionExpired'
import { useActiveTenant } from '@/tenant/context'
import { SupportAccessBanner } from '@/shell/SupportAccessBanner'
import { SupportReadOnlyNotice } from '@/shell/SupportReadOnlyNotice'
import { AccountStatus } from '@/pages/AccountStatus'
import { SetupBanner } from './SetupBanner'
import { ModuleSwitcher, Sidebar } from './Sidebar'
import { AccountItems, AccountWho, TopBar } from './TopBar'
import type { Crumb } from './TopBar'
import { countFor, useNavCounts } from './counts'
import { useHashScroll } from './useHashScroll'
import type { NavCounts } from './counts'
import {
  MODULE_STORAGE_KEY,
  isCurrent,
  mobileSections,
  moduleById,
  moduleForPath,
  rememberedModule,
  sectionForPath,
} from './navigation'
import type { ModuleId } from './navigation'

/**
 * The frame every signed-in screen sits inside.
 *
 * Converted once, from S-D3, so no later session redraws navigation — which is
 * the whole reason this session exists ahead of the module sessions.
 *
 * The module is derived from the URL where the URL says (`/seo`, `/outreach`)
 * and from the remembered choice where it does not (`/billing`, which belongs
 * to both). Storing it is what stops a billing visit from silently moving
 * somebody out of the module they were working in — and it is what the
 * settings screen's *Back* link reads, now that settings is a page of its own
 * outside this frame (`SettingsShell`, owner decision 2026-09-16).
 */
/** The billing routes' crumb leaves; `/billing` itself is the invoices screen. */
const BILLING_LEAVES: Record<string, string> = {
  '/billing/plans': 'Plans',
  '/billing/usage': 'Usage',
  '/billing/cancel': 'Cancel',
  '/billing/return': 'Checkout',
}

export function AppShell(): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  const { tenant } = useActiveTenant()
  const { expired } = useAuth()
  const [drawerOpen, setDrawerOpen] = useState(false)
  // SHELL-1. Read once here and handed to both rails — see `useNavCounts`.
  const counts = useNavCounts()

  // `safeStorage()` and not `window.localStorage`: the initialiser below runs
  // during render, and the storage getter itself throws where storage is
  // blocked for the origin, which would blank the shell (WEB-8).
  const storage = useMemo(() => safeStorage(), [])
  const fromPath = moduleForPath(location.pathname)
  const [remembered, setRemembered] = useState<ModuleId>(() => rememberedModule(storage))
  const module = fromPath ?? remembered

  useEffect(() => {
    if (fromPath) {
      setRemembered(fromPath)
      storage.setItem(MODULE_STORAGE_KEY, fromPath)
    }
  }, [fromPath, storage])

  // The drawer is a route-level overlay; leaving it open across a navigation
  // would cover the screen the user just asked for.
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  useHashScroll()

  const onModuleChange = useCallback(
    (id: ModuleId) => {
      setRemembered(id)
      storage.setItem(MODULE_STORAGE_KEY, id)
      void navigate(moduleById(id).home)
    },
    [navigate, storage],
  )

  const crumbs = useMemo<Crumb[]>(() => {
    const organisation: Crumb = { label: tenant.name, to: '/organisations' }

    // Billing is the same organisation's billing whichever module you came
    // from, and its screens used to inherit *SEO / Overview* (parity P-PLAN-01,
    // P-USE-07). The leaf is the screen's own name.
    if (location.pathname.startsWith('/billing')) {
      const leaf = BILLING_LEAVES[location.pathname]
      return leaf === undefined
        ? [organisation, { label: 'Billing' }]
        : [organisation, { label: 'Billing', to: '/billing' }, { label: leaf }]
    }

    const definition = moduleById(module)
    // S-B19. Read off the navigation model rather than hardcoded, because the
    // Inbox is the first section route and *Overview* was being shown on it.
    // A section that gets a route from here on gets its own breadcrumb with no
    // edit to this file. `Overview` is still the fallback: it is what the
    // module home is called, and it is what `/billing/*` — inside the shell,
    // not a module section — has always shown.
    const section = sectionForPath(location.pathname)
    return [
      organisation,
      { label: definition.label, to: definition.home },
      { label: section?.label ?? 'Overview' },
    ]
  }, [module, tenant.name, location.pathname])

  return (
    <div className="app-shell">
      {/* S-B11. Outside the routed area and above the sidebar, so no screen can
          omit it and it cannot be scrolled away from — which is what makes screen
          08's "the banner cannot be dismissed" a property of the layout rather
          than a promise. `null` on every ordinary session. */}
      <SupportAccessBanner />

      <Sidebar module={module} counts={counts} onModuleChange={onModuleChange} />

      <div className="app-main">
        <TopBar
          crumbs={crumbs}
          onOpenMenu={() => {
            setDrawerOpen(true)
          }}
        />

        {/* SHELL-5. The switcher, inline below 1100px. `shell.html:242-246`
            draws it under the compact top bar and the screen's own note says
            why it must survive the collapse: it is a **mode**, not navigation,
            and losing it would strand somebody in the wrong half of the
            product. It was reachable only from inside the drawer, which is two
            presses to answer a question the design answers at a glance.

            Rendered here rather than inside `TopBar` so it sits below the bar's
            border exactly as drawn, and hidden above 1100px by `.app-switch-bar`
            — where the sidebar carries the same control eight pixels away. */}
        <div className="app-switch-bar" role="region" aria-label="Module">
          <ModuleSwitcher active={module} label="Module (compact)" onChange={onModuleChange} />
        </div>

        <main className="app-body">
          <SetupBanner />
          {/* S-B32a. **The unconfirmed-address banner, moved here from
              `ModuleOverview`.** It used to render from the module landing
              placeholder, which meant it lived on whichever screens had not been
              built yet: S-B31a replaced the outreach one and S-B32a the SEO one,
              and between them the banner would have left the product entirely.

              The shell is where it always belonged. It is an *account* fact, not
              a module one — an unconfirmed address is why approval requests,
              digests and password resets silently never arrive, which presents
              to the user as "AugRank does not send me anything" — so it must not
              be something a future screen can drop by being written. It renders
              nothing when there is nothing to say. */}
          <AccountStatus />
          {/* SUP-40. Screen 08's second notice — the one inside the tenant frame
              that says what a read-only support session can and cannot do. Here
              rather than in each screen for `AccountStatus`'s reason: a screen
              that has not been written yet cannot forget it. `null` on every
              ordinary session. */}
          <SupportReadOnlyNotice />
          <Outlet />
        </main>

        <MobileBar
          module={module}
          counts={counts}
          moreOpen={drawerOpen}
          onOpenMore={() => {
            setDrawerOpen(true)
          }}
        />
      </div>

      {drawerOpen ? (
        <div
          className="ar-drawer"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDrawerOpen(false)
          }}
        >
          <div className="ar-drawer__panel" style={{ inset: '0 auto 0 0' }}>
            <Sidebar
              module={module}
              counts={counts}
              variant="drawer"
              onModuleChange={onModuleChange}
              onNavigate={() => {
                setDrawerOpen(false)
              }}
            />
            {/* Parity P-MOB-01 (2026-09-13). The compact top bar has no avatar,
                so the account rows live here — `AccountItems` on why the same
                rows serve both. A plain block, not a `role="menu"`: it is part
                of a scrolling drawer, not a popup with its own arrow keys. */}
            <div className="app-drawer__account">
              <AccountWho />
              <div className="app-menu__items">
                <AccountItems
                  onNavigate={() => {
                    setDrawerOpen(false)
                  }}
                />
              </div>
            </div>
            <div className="ar-drawer__footer">
              <button
                type="button"
                className="ar-btn ar-btn--ghost ar-btn--block"
                onClick={() => {
                  setDrawerOpen(false)
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {expired ? <SessionExpired /> : null}
    </div>
  )
}

/**
 * The bottom bar below 1100px — SHELL-4 and SHELL-6.
 *
 * **Three sections and a *More*, not the module's first four.** Taking the
 * first four gave SEO *Overview, Search performance, Sites, Keywords* — an
 * accident of array order, and it left Approvals off the one surface the design
 * says a tenant opens their phone for. `mobileSections` names them instead, and
 * the fourth slot opens the drawer holding everything else: before this there
 * was no way to a section outside the first four except by finding the ☰ in the
 * top bar, which is the control the bottom bar exists to spare people.
 *
 * *More* is a button rather than a link because it opens an overlay rather than
 * navigating; `aria-expanded` says which, and the drawer closes itself on any
 * navigation inside it.
 *
 * Sections whose module has not shipped keep their place and say so, for the
 * same reason the sidebar does.
 */
function MobileBar({
  module,
  counts,
  moreOpen,
  onOpenMore,
}: {
  module: ModuleId
  counts: NavCounts | null
  /** Whether the drawer *More* opens is showing, for `aria-expanded`. */
  moreOpen: boolean
  onOpenMore: () => void
}): JSX.Element {
  const definition = moduleById(module)
  const { pathname } = useLocation()
  return (
    <nav className="app-mobile-bar" aria-label={`${definition.label} (compact)`}>
      {mobileSections(module).map((section) => {
        const count = countFor(counts, section.count)
        return section.to ? (
          <Link
            key={section.label}
            to={section.to}
            aria-current={isCurrent(section, pathname, definition.home) ? 'page' : undefined}
          >
            {section.label}
            {/* Parity P-MOB-03 (2026-09-13). `shell.html:262-265` draws the bar
                with a badge on Approvals and nothing on Keywords — the count is
                the sidebar's, and only the section flagged `badge` is the one
                somebody has to act on. */}
            {count === null || !section.badge ? null : (
              <span className="ar-badge ar-badge--warning app-mobile-bar__badge">
                {count}
                <span className="ar-visually-hidden"> waiting</span>
              </span>
            )}
          </Link>
        ) : (
          <span key={section.label} aria-disabled="true">
            {section.label}
          </span>
        )
      })}
      <button
        type="button"
        className="app-mobile-bar__more"
        aria-expanded={moreOpen}
        onClick={onOpenMore}
      >
        More
      </button>
    </nav>
  )
}
